import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
	Commission, 
	CommissionCreateDto, 
	CommissionBackendResponse,
	CommissionState, 
	CommissionRateModifier, 
	CommissionRateModifierCreateDto, 
	CommissionRateModifierUpdateDto,
	CommissionTierCreateDto,
	CommissionTierUpdateDto,
	CommissionTierBackendResponse
} from "../../modules/shared/model/commission.interfaces";
import { 
	createCommission as createCommissionAction, 
	updateCommission as updateCommissionAction, 
	getCommissionById as getCommissionByIdAction, 
	getCommissions as getCommissionsAction, 
	deleteCommission as deleteCommissionAction, 
	getCommissionStats as getCommissionStatsAction,
	createCommissionRateModifier as createCommissionRateModifierAction,
	updateCommissionRateModifier as updateCommissionRateModifierAction,
	getCommissionRateModifierById as getCommissionRateModifierByIdAction,
	getCommissionRateModifiers as getCommissionRateModifiersAction,
	deleteCommissionRateModifier as deleteCommissionRateModifierAction,
	createCommissionTier as createCommissionTierAction,
	updateCommissionTier as updateCommissionTierAction,
	getCommissionTierById as getCommissionTierByIdAction,
	getCommissionTiers as getCommissionTiersAction,
	deleteCommissionTier as deleteCommissionTierAction
} from "../Actions/CommissionActions";

export const createCommission = createAsyncThunk("commissions/create", async (data: CommissionCreateDto, { rejectWithValue }) => {
	try { 
		return await createCommissionAction(data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to create commission"); 
	}
});

export const updateCommission = createAsyncThunk("commissions/update", async ({ id, data }: { id: string; data: Partial<CommissionCreateDto> }, { rejectWithValue }) => {
	try { 
		return await updateCommissionAction(id, data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to update commission"); 
	}
});

export const fetchCommission = createAsyncThunk("commissions/getById", async (id: string, { rejectWithValue }) => {
	try { 
		return await getCommissionByIdAction(id); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch commission"); 
	}
});

export const fetchCommissions = createAsyncThunk("commissions/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { 
		return await getCommissionsAction(query); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch commissions"); 
	}
});

export const deleteCommission = createAsyncThunk("commissions/delete", async (id: string, { rejectWithValue }) => {
	try { 
		await deleteCommissionAction(id); 
		return id; 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to delete commission"); 
	}
});

export const fetchCommissionStats = createAsyncThunk("commissions/stats", async (_, { rejectWithValue }) => {
	try { 
		return await getCommissionStatsAction(); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch commission stats"); 
	}
});

// Commission Rate Modifier Async Thunks
export const createCommissionRateModifier = createAsyncThunk("commissionRateModifiers/create", async (data: CommissionRateModifierCreateDto, { rejectWithValue }) => {
	try { 
		return await createCommissionRateModifierAction(data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to create commission rate modifier"); 
	}
});

export const updateCommissionRateModifier = createAsyncThunk("commissionRateModifiers/update", async ({ id, data }: { id: string; data: CommissionRateModifierUpdateDto }, { rejectWithValue }) => {
	try { 
		return await updateCommissionRateModifierAction(id, data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to update commission rate modifier"); 
	}
});

export const fetchCommissionRateModifier = createAsyncThunk("commissionRateModifiers/getById", async (id: string, { rejectWithValue }) => {
	try { 
		return await getCommissionRateModifierByIdAction(id); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch commission rate modifier"); 
	}
});

export const fetchCommissionRateModifiers = createAsyncThunk("commissionRateModifiers/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { 
		return await getCommissionRateModifiersAction(query); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch commission rate modifiers"); 
	}
});

export const deleteCommissionRateModifier = createAsyncThunk("commissionRateModifiers/delete", async (id: string, { rejectWithValue }) => {
	try { 
		await deleteCommissionRateModifierAction(id); 
		return id; 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to delete commission rate modifier"); 
	}
});

// Commission Tier Async Thunks
export const createCommissionTier = createAsyncThunk("commissionTiers/create", async (data: CommissionTierCreateDto, { rejectWithValue }) => {
	try { 
		return await createCommissionTierAction(data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to create commission tier"); 
	}
});

export const updateCommissionTier = createAsyncThunk("commissionTiers/update", async ({ id, data }: { id: string; data: CommissionTierUpdateDto }, { rejectWithValue }) => {
	try { 
		return await updateCommissionTierAction(id, data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to update commission tier"); 
	}
});

export const fetchCommissionTier = createAsyncThunk("commissionTiers/getById", async (id: string, { rejectWithValue }) => {
	try { 
		return await getCommissionTierByIdAction(id); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch commission tier"); 
	}
});

export const fetchCommissionTiers = createAsyncThunk("commissionTiers/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { 
		return await getCommissionTiersAction(query); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch commission tiers"); 
	}
});

export const deleteCommissionTier = createAsyncThunk("commissionTiers/delete", async (id: string, { rejectWithValue }) => {
	try { 
		await deleteCommissionTierAction(id); 
		return id; 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to delete commission tier"); 
	}
});

const initialState: CommissionState = {
	items: [],
	current: null,
	loading: false,
	error: null,
	totalCount: 0,
	rateModifiers: [],
	rateModifiersLoading: false,
	rateModifiersError: null,
	commissionTiers: [],
	commissionTiersLoading: false,
	commissionTiersError: null,
};

const commissionSlice = createSlice({
	name: "commissions",
	initialState,
	reducers: {
		clearCommissionError(state) {
			state.error = null;
		},
		clearCurrentCommission(state) {
			state.current = null;
		},
		clearRateModifiersError(state) {
			state.rateModifiersError = null;
		},
		clearCommissionTiersError(state) {
			state.commissionTiersError = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(createCommission.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(createCommission.fulfilled, (s, a: PayloadAction<CommissionBackendResponse>) => { 
				s.loading = false; 
				s.items.unshift(a.payload); 
				s.totalCount = (s.totalCount || 0) + 1; 
			})
			.addCase(createCommission.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(updateCommission.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(updateCommission.fulfilled, (s, a: PayloadAction<CommissionBackendResponse>) => {
				s.loading = false;
				const i = s.items.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.items[i] = a.payload;
				if (s.current?.id === a.payload.id) s.current = a.payload;
			})
			.addCase(updateCommission.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(fetchCommission.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(fetchCommission.fulfilled, (s, a: PayloadAction<CommissionBackendResponse>) => { 
				s.loading = false; 
				s.current = a.payload; 
			})
			.addCase(fetchCommission.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(fetchCommissions.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(fetchCommissions.fulfilled, (s, a: PayloadAction<{ data: CommissionBackendResponse[]; totalCount?: number }>) => { 
				s.loading = false; 
				s.items = a.payload.data || []; 
				s.totalCount = a.payload.totalCount || 0; 
			})
			.addCase(fetchCommissions.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(deleteCommission.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(deleteCommission.fulfilled, (s, a: PayloadAction<string>) => { 
				s.loading = false; 
				s.items = s.items.filter(x => x.id !== a.payload); 
				if (s.current?.id === a.payload) s.current = null; 
				s.totalCount = Math.max(0, (s.totalCount || 0) - 1); 
			})
			.addCase(deleteCommission.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		// Commission Rate Modifier Reducers
		builder
			.addCase(createCommissionRateModifier.pending, (s) => { 
				s.rateModifiersLoading = true; 
				s.rateModifiersError = null; 
			})
			.addCase(createCommissionRateModifier.fulfilled, (s, a: PayloadAction<CommissionRateModifier>) => { 
				s.rateModifiersLoading = false; 
				s.rateModifiers.unshift(a.payload); 
			})
			.addCase(createCommissionRateModifier.rejected, (s, a: any) => { 
				s.rateModifiersLoading = false; 
				s.rateModifiersError = a.payload as string; 
			});

		builder
			.addCase(updateCommissionRateModifier.pending, (s) => { 
				s.rateModifiersLoading = true; 
				s.rateModifiersError = null; 
			})
			.addCase(updateCommissionRateModifier.fulfilled, (s, a: PayloadAction<CommissionRateModifier>) => {
				s.rateModifiersLoading = false;
				const i = s.rateModifiers.findIndex(x => x.commission.id === a.payload.commission.id);
				if (i !== -1) s.rateModifiers[i] = a.payload;
			})
			.addCase(updateCommissionRateModifier.rejected, (s, a: any) => { 
				s.rateModifiersLoading = false; 
				s.rateModifiersError = a.payload as string; 
			});

		builder
			.addCase(fetchCommissionRateModifier.pending, (s) => { 
				s.rateModifiersLoading = true; 
				s.rateModifiersError = null; 
			})
			.addCase(fetchCommissionRateModifier.fulfilled, (s, a: PayloadAction<CommissionRateModifier>) => { 
				s.rateModifiersLoading = false; 
				// Update or add the rate modifier
				const existingIndex = s.rateModifiers.findIndex(rm => rm.commission.id === a.payload.commission.id);
				if (existingIndex !== -1) {
					s.rateModifiers[existingIndex] = a.payload;
				} else {
					s.rateModifiers.push(a.payload);
				}
			})
			.addCase(fetchCommissionRateModifier.rejected, (s, a: any) => { 
				s.rateModifiersLoading = false; 
				s.rateModifiersError = a.payload as string; 
			});

		builder
			.addCase(fetchCommissionRateModifiers.pending, (s) => { 
				s.rateModifiersLoading = true; 
				s.rateModifiersError = null; 
			})
			.addCase(fetchCommissionRateModifiers.fulfilled, (s, a: PayloadAction<{ data: CommissionRateModifier[]; totalCount?: number }>) => { 
				s.rateModifiersLoading = false; 
				s.rateModifiers = a.payload.data || []; 
			})
			.addCase(fetchCommissionRateModifiers.rejected, (s, a: any) => { 
				s.rateModifiersLoading = false; 
				s.rateModifiersError = a.payload as string; 
			});

		builder
			.addCase(deleteCommissionRateModifier.pending, (s) => { 
				s.rateModifiersLoading = true; 
				s.rateModifiersError = null; 
			})
			.addCase(deleteCommissionRateModifier.fulfilled, (s, a: PayloadAction<string>) => { 
				s.rateModifiersLoading = false; 
				s.rateModifiers = s.rateModifiers.filter(rm => rm.commission.id !== a.payload); 
			})
			.addCase(deleteCommissionRateModifier.rejected, (s, a: any) => { 
				s.rateModifiersLoading = false; 
				s.rateModifiersError = a.payload as string; 
			});

		// Commission Tier Reducers
		builder
			.addCase(createCommissionTier.pending, (s) => { 
				s.commissionTiersLoading = true; 
				s.commissionTiersError = null; 
			})
			.addCase(createCommissionTier.fulfilled, (s, a: PayloadAction<CommissionTierBackendResponse>) => { 
				s.commissionTiersLoading = false; 
				s.commissionTiers.unshift(a.payload); 
			})
			.addCase(createCommissionTier.rejected, (s, a: any) => { 
				s.commissionTiersLoading = false; 
				s.commissionTiersError = a.payload as string; 
			});

		builder
			.addCase(updateCommissionTier.pending, (s) => { 
				s.commissionTiersLoading = true; 
				s.commissionTiersError = null; 
			})
			.addCase(updateCommissionTier.fulfilled, (s, a: PayloadAction<CommissionTierBackendResponse>) => {
				s.commissionTiersLoading = false;
				const i = s.commissionTiers.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.commissionTiers[i] = a.payload;
			})
			.addCase(updateCommissionTier.rejected, (s, a: any) => { 
				s.commissionTiersLoading = false; 
				s.commissionTiersError = a.payload as string; 
			});

		builder
			.addCase(fetchCommissionTier.pending, (s) => { 
				s.commissionTiersLoading = true; 
				s.commissionTiersError = null; 
			})
			.addCase(fetchCommissionTier.fulfilled, (s, a: PayloadAction<CommissionTierBackendResponse>) => { 
				s.commissionTiersLoading = false; 
				// Store current tier if needed
			})
			.addCase(fetchCommissionTier.rejected, (s, a: any) => { 
				s.commissionTiersLoading = false; 
				s.commissionTiersError = a.payload as string; 
			});

		builder
			.addCase(fetchCommissionTiers.pending, (s) => { 
				s.commissionTiersLoading = true; 
				s.commissionTiersError = null; 
			})
			.addCase(fetchCommissionTiers.fulfilled, (s, a: PayloadAction<{ data: CommissionTierBackendResponse[]; totalCount?: number }>) => { 
				s.commissionTiersLoading = false; 
				s.commissionTiers = a.payload.data || []; 
			})
			.addCase(fetchCommissionTiers.rejected, (s, a: any) => { 
				s.commissionTiersLoading = false; 
				s.commissionTiersError = a.payload as string; 
			});

		builder
			.addCase(deleteCommissionTier.pending, (s) => { 
				s.commissionTiersLoading = true; 
				s.commissionTiersError = null; 
			})
			.addCase(deleteCommissionTier.fulfilled, (s, a: PayloadAction<string>) => { 
				s.commissionTiersLoading = false; 
				s.commissionTiers = s.commissionTiers.filter(ct => ct.id !== a.payload); 
			})
			.addCase(deleteCommissionTier.rejected, (s, a: any) => { 
				s.commissionTiersLoading = false; 
				s.commissionTiersError = a.payload as string; 
			});
	}
});

export const { clearCommissionError, clearCurrentCommission, clearRateModifiersError, clearCommissionTiersError } = commissionSlice.actions;
export default commissionSlice.reducer;
